import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useInsuranceStore, type InsuranceInfo, type IInsImage } from "@/store/insurance";

const EMPTY_IMAGE: IInsImage = {
  id: 0,
  description: "",
  fileID: 0,
  fileName: "",
  base64: "",
  extension: "",
};

/** Build a full InsuranceInfo with empty defaults + the caller's overrides (e.g. aa/wc tags). */
export function makeInitialInsurance(overrides: Partial<InsuranceInfo> = {}): InsuranceInfo {
  return {
    id: 0,
    relation: "",
    firstName: "",
    mi: "",
    lastName: "",
    dob: "",
    carrier: "",
    carrierPhone: "",
    empName: "",
    empPhone: "",
    idNum: "",
    groupNo: "",
    aa: 0,
    wc: 0,
    medPay: 0,
    attName: "",
    attPhone: "",
    images: { front: { ...EMPTY_IMAGE }, back: { ...EMPTY_IMAGE } },
    ...overrides,
  };
}

/** New-record templates, tagged so records land in the right bucket (matches the Vue originals). */
export const INITIAL_AUTO = makeInitialInsurance({ aa: 1, wc: -1 });
export const INITIAL_WC = makeInitialInsurance({ aa: -1, wc: 1 });
export const INITIAL_HEALTH = makeInitialInsurance({ aa: 0, wc: 0 });

/** True when a record is auto-accident or workers-comp (numeric-safe: backend may return strings). */
export function isAccidentOrWcRecord(r: InsuranceInfo): boolean {
  return Number(r.aa) === 1 || Number(r.wc) === 1;
}

/**
 * Policy-holder options. Backend maps the STRING relation → RelID (Self=1, Spouse=2, Child=3),
 * so "Parent/Guardian" (patient is the child) must send value "Child" or the relationship is lost.
 */
export const RELATION_OPTIONS: { value: string; label: string }[] = [
  { value: "Self", label: "Self" },
  { value: "Spouse", label: "Spouse" },
  { value: "Child", label: "Parent/Guardian" },
];

/** Normalize a stored relation back to a known option value. */
export function normalizeRelation(r: string | undefined | null): string {
  if (!r) return "";
  if (r === "Parent/Guardian") return "Child";
  if (r === "Self" || r === "Spouse" || r === "Child") return r;
  return "";
}

/** Policy-holder name + DOB are required whenever the holder is not the patient. */
export const relationRequiresName = (r: string): boolean => !!r && r !== "Self";

const SAVE_DEBOUNCE_MS = 500;

export interface SingleInsuranceRecordConfig {
  /** New-record template with correct aa/wc tags (INITIAL_AUTO / INITIAL_WC). */
  initial: InsuranceInfo;
  /** Pick this form's existing record out of the full list (e.g. r => Number(r.aa) === 1). */
  select: (records: InsuranceInfo[]) => InsuranceInfo | undefined;
}

export interface SingleInsuranceRecord {
  loading: boolean;
  /** The prefilled record, or null if none exists yet. */
  record: InsuranceInfo | null;
  /** Debounced per-field save; creates the record on the first edit (once). */
  saveField: (field: string, value: string | number) => void;
  /** Immediately persist any pending debounced edits (call before navigating away). */
  flush: () => Promise<void>;
}

/**
 * Manages ONE insurance record (Auto / Workers' Comp): prefill, create-on-first-edit
 * (guarded so rapid edits / StrictMode can't spawn duplicates), and debounced per-field save.
 * Health Insurance uses its own multi-record logic but the same store methods.
 */
export function useSingleInsuranceRecord(config: SingleInsuranceRecordConfig): SingleInsuranceRecord {
  const fetchInsuranceInfo = useInsuranceStore((s) => s.fetchInsuranceInfo);
  const updateInsuranceInfo = useInsuranceStore((s) => s.updateInsuranceInfo);
  const updateInsuranceInfoField = useInsuranceStore((s) => s.updateInsuranceInfoField);

  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState<InsuranceInfo | null>(null);

  const cfgRef = useRef(config);
  cfgRef.current = config;
  const idRef = useRef(0);
  const createPromiseRef = useRef<Promise<number> | null>(null);
  const timersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const pendingRef = useRef<Record<string, string | number>>({});

  // Prefill once on mount. Always fetches fresh (never trusts persisted store state),
  // so a prior patient's data can't leak in.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = (await fetchInsuranceInfo()) as InsuranceInfo[] | null | undefined;
        if (cancelled) return;
        const found = Array.isArray(list) ? cfgRef.current.select(list) : undefined;
        if (found) {
          idRef.current = found.id;
          setRecord(found);
        }
      } catch {
        /* prefill is best-effort */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    const timers = timersRef.current;
    return () => {
      cancelled = true;
      Object.values(timers).forEach(clearTimeout);
    };
    // Run once: store fns are stable; config is read via ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Create the record exactly once; concurrent callers share the same in-flight promise. */
  const ensureRecord = useCallback(async (): Promise<number> => {
    if (idRef.current) return idRef.current;
    if (createPromiseRef.current) return createPromiseRef.current;
    const p = (async () => {
      const created = (await updateInsuranceInfo(cfgRef.current.initial)) as InsuranceInfo | null;
      const newId = created?.id ?? 0;
      if (newId) {
        idRef.current = newId;
        setRecord((prev) => ({ ...(prev ?? cfgRef.current.initial), ...(created ?? {}), id: newId }));
      }
      return newId;
    })();
    createPromiseRef.current = p;
    try {
      return await p;
    } finally {
      createPromiseRef.current = null;
    }
  }, [updateInsuranceInfo]);

  const runSave = useCallback(
    async (field: string) => {
      if (!(field in pendingRef.current)) return;
      const value = pendingRef.current[field];
      delete pendingRef.current[field];
      try {
        const id = await ensureRecord();
        if (!id) {
          toast.error("Something went wrong saving your info.");
          return;
        }
        await updateInsuranceInfoField(id, field, value);
      } catch {
        toast.error("Something went wrong saving your info.");
      }
    },
    [ensureRecord, updateInsuranceInfoField]
  );

  const saveField = useCallback(
    (field: string, value: string | number) => {
      pendingRef.current[field] = value;
      clearTimeout(timersRef.current[field]);
      timersRef.current[field] = setTimeout(() => {
        delete timersRef.current[field];
        void runSave(field);
      }, SAVE_DEBOUNCE_MS);
    },
    [runSave]
  );

  /** Persist any pending debounced edits now (call before navigating away). */
  const flush = useCallback(async () => {
    const fields = Object.keys(pendingRef.current);
    for (const f of fields) {
      clearTimeout(timersRef.current[f]);
      delete timersRef.current[f];
    }
    await Promise.all(fields.map((f) => runSave(f)));
  }, [runSave]);

  return { loading, record, saveField, flush };
}
